import { useTranslation } from "react-i18next";

import { Page } from "@/components/page";
import {
  useAdminMcpIssues,
  useAdminMcpIssuesAttention,
  useAdminMcpUsage,
  useConnectorOverview,
  useMe,
} from "@/lib/api";
import { belgianHour } from "@/lib/format";
import { isAdminRole } from "@/lib/roles";
import { USAGE_DAYS } from "@/lib/usage-window";

import { ConnectorCard, QUARTER } from "./connector-card";
import { DocsPointer } from "./docs-pointer";
import { RecentUse } from "./recent-use";
import { greeting } from "./utils";

/**
 * Home: a greeting and the connector's status for everyone, with, for an
 * admin, the failures still to analyse under it (the issues that are new,
 * open again or come back, each with the way to its page, and how many
 * Velta is on); then, for admins, the last 7 days' use against its failures
 * (with the way to Statistieken), and for everyone else the way to the docs.
 * The tools have their own page, and how to connect Claude is in the docs.
 */
export function HomePage() {
  const { data: me } = useMe();
  // Re-renders the greeting on a language switch: it is read at render.
  useTranslation();
  const isAdmin = isAdminRole(me?.role);
  const overviewQuery = useConnectorOverview();
  // Usage is admin-only on the backend, so only admins ask for it.
  const usageQuery = useAdminMcpUsage(USAGE_DAYS, { enabled: isAdmin });
  const attentionQuery = useAdminMcpIssuesAttention({ enabled: isAdmin });
  // The last quarter's issues, as Fouten lists them: what its links count.
  const quarterQuery = useAdminMcpIssues(QUARTER, { enabled: isAdmin });
  // Nothing while it loads or after it failed: a stale issue is worse than none.
  const attention = isAdmin && !attentionQuery.isError ? attentionQuery.data : undefined;
  const quarterIssues = isAdmin && !quarterQuery.isError ? quarterQuery.data?.issues : undefined;

  return (
    <Page>
      <header className="mb-8">
        <h1 className="font-heading text-primary text-2xl font-bold">
          {greeting(belgianHour())}
          {me?.first_name ? `, ${me.first_name}` : ""}
        </h1>
      </header>

      {/* One column of `minmax(0, 1fr)` below `lg`, so a card never widens the page. */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        <ConnectorCard
          overview={overviewQuery.data}
          isLoading={overviewQuery.isLoading}
          onRetry={() => void overviewQuery.refetch()}
          attention={attention}
          quarterIssues={quarterIssues}
        />
        {isAdmin ? (
          <RecentUse
            days={USAGE_DAYS}
            usage={usageQuery.data}
            isLoading={usageQuery.isLoading}
            onRetry={() => void usageQuery.refetch()}
            // As tall as its chart: beside a long list of issues it would otherwise stretch empty.
            className="lg:col-span-2 lg:self-start"
          />
        ) : (
          <DocsPointer className="lg:col-span-2" />
        )}
      </div>
    </Page>
  );
}
